import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import GetAppIcon from "@material-ui/icons/GetApp";
import DeleteIcon from "@material-ui/icons/Delete";
import IconAction from "./IconAction";
import { getFormattedDate } from "../../../../../Utils/Helpers/dateUtils";
import { getFileBadgeClass } from "../DocumentsDashboard/DocumentsDashboard.helper";
// Same file-type badge look as the loan Documents Dashboard (LLD §3.3) — imported, not copied,
// so a PDF looks identical in both places and the two never drift.
import "../DocumentsDashboard/DocumentsDashboard.css";
import useStyles from "./ProfessionalContacts.styles";

// Exported (FIRM-DOCUMENTS-020, LLD §0.4) so FirmDocumentsAllDialog's "View all" table shares the
// exact same columns/comparator/row markup instead of a second copy that could drift.
export const COLUMNS = [
  { key: "fileName", label: "Name" },
  { key: "uploadedBy", label: "Uploaded by" },
  { key: "uploadedUtc", label: "Uploaded on" },
];

const NAME_TRUNCATE_LENGTH = 40;

const truncate = (value, max) =>
  value && value.length > max ? `${value.slice(0, max - 1)}…` : value || "";

const extensionOf = (document) => {
  const raw =
    document.fileExtension ||
    (document.fileName || "").slice((document.fileName || "").lastIndexOf("."));
  return (raw || "").replace(".", "").toLowerCase();
};

/** Pure comparator, shared by the inline table and the "View all" dialog — each keeps its own `order` state. */
export const sortDocuments = (documents, order) => {
  const list = [...documents];
  list.sort((a, b) => {
    const av = (a[order.key] || "").toString();
    const bv = (b[order.key] || "").toString();
    const cmp = av.localeCompare(bv, undefined, {
      numeric: true,
      sensitivity: "base",
    });
    return order.dir === "asc" ? cmp : -cmp;
  });
  return list;
};

/** One document row — shared so the inline table and the "View all" dialog render identically. */
export const DocumentTableRow = ({ document, busyId, onDownload, onDelete }) => {
  const classes = useStyles();
  const busy = busyId === document.documentId;
  const ext = extensionOf(document);
  return (
    <TableRow>
      <TableCell className={classes.cellStrong}>
        <span className={classes.documentNameCell} title={document.fileName}>
          <span
            className={clsx(
              "fileTypeBadge",
              classes.documentIcon,
              getFileBadgeClass(ext)
            )}
          >
            {ext || "file"}
          </span>
          {truncate(document.fileName, NAME_TRUNCATE_LENGTH)}
        </span>
      </TableCell>
      <TableCell>{document.uploadedBy}</TableCell>
      <TableCell className={classes.cellNowrap}>
        {getFormattedDate(document.uploadedUtc, "DD/MM/YYYY, HH:mm")}
      </TableCell>
      <TableCell align="right" className={clsx(classes.cellActions, classes.cellNowrap)}>
        <IconAction
          title={`Download ${document.fileName}`}
          icon={<GetAppIcon fontSize="small" />}
          disabled={busy}
          onClick={() => onDownload(document)}
        />
        <IconAction
          danger
          title={`Delete ${document.fileName}`}
          icon={<DeleteIcon fontSize="small" />}
          disabled={busy}
          onClick={() => onDelete(document)}
        />
      </TableCell>
    </TableRow>
  );
};

DocumentTableRow.propTypes = {
  document: PropTypes.object.isRequired,
  busyId: PropTypes.string,
  onDownload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

/**
 * The many-rows half of the Documents section (LLD §3.3) — structurally the same component as
 * BankAccountsTable, only the columns differ.
 */
const FirmDocumentsTable = ({ documents, onDownload, onDelete, busyId }) => {
  const classes = useStyles();
  const [order, setOrder] = useState({ key: "uploadedUtc", dir: "desc" });

  const sorted = useMemo(
    () => sortDocuments(documents, order),
    [documents, order]
  );

  const handleSort = (key) => {
    setOrder((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );
  };

  return (
    <div className={classes.tableContainer}>
      <Table size="small" className={classes.table}>
        <TableHead className={classes.tableHead}>
          <TableRow>
            {COLUMNS.map((col) => (
              <TableCell
                key={col.key}
                sortDirection={order.key === col.key ? order.dir : false}
              >
                <TableSortLabel
                  className={classes.sortLabel}
                  active={order.key === col.key}
                  direction={order.key === col.key ? order.dir : "asc"}
                  onClick={() => handleSort(col.key)}
                >
                  {col.label}
                </TableSortLabel>
              </TableCell>
            ))}
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody className={classes.tableBody}>
          {sorted.map((document) => (
            <DocumentTableRow
              key={document.documentId}
              document={document}
              busyId={busyId}
              onDownload={onDownload}
              onDelete={onDelete}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

FirmDocumentsTable.propTypes = {
  documents: PropTypes.array.isRequired,
  onDownload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  busyId: PropTypes.string,
};

export default FirmDocumentsTable;
